import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { WIDGET_REGISTRY } from "@/lib/widget-registry";

interface AddWidgetDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddWidgetDialog({ isOpen, onClose }: AddWidgetDialogProps) {
  const addWidgetMutation = useMutation({
    mutationFn: async (widgetType: string) => {
      const config = WIDGET_REGISTRY[widgetType];
      return apiRequest("POST", "/api/dashboard/widgets", {
        widgetType,
        size: config.defaultSize,
        settings: config.defaultSettings,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/widgets"] });
      onClose();
    },
    onError: () => {
    },
  });

  const handleAddWidget = (widgetType: string) => {
    addWidgetMutation.mutate(widgetType);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Widget</DialogTitle>
          <DialogDescription>
            Choose a widget to add to your dashboard
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 gap-3 max-h-[60vh] overflow-y-auto">
          {Object.values(WIDGET_REGISTRY).map((widget) => {
            const Icon = widget.icon;
            return (
              <div
                key={widget.type}
                className="flex items-start gap-4 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className={`w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-foreground mb-1">
                    {widget.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {widget.description}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleAddWidget(widget.type)}
                  disabled={addWidgetMutation.isPending}
                  data-testid={`button-add-widget-${widget.type}`}
                >
                  Add
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
